import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button, ErrorBox, Field, Input, SuccessBox } from "@/components/ui";
import { authPost } from "@/lib/api";
import { AuthCard } from "./auth";

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const email = String(new FormData(e.currentTarget).get("email"));
    setBusy(true);
    setError(null);
    try {
      await authPost("/request-password-reset", { email, redirectTo: `${window.location.origin}/reset-password` });
      setSent(email);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthCard title={t("auth.forgotTitle")} subtitle={sent ? undefined : t("auth.forgotSubtitle")}>
      {sent ? (
        <div className="space-y-4">
          <SuccessBox>{t("auth.forgotSent", { email: sent })}</SuccessBox>
          <BackToLogin />
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label={t("common.email")} htmlFor="email">
            <Input id="email" name="email" type="email" required autoComplete="email" autoFocus />
          </Field>
          <ErrorBox error={error} />
          <Button type="submit" className="w-full justify-center" loading={busy}>
            {t("auth.forgotSubmit")}
          </Button>
          <BackToLogin />
        </form>
      )}
    </AuthCard>
  );
}

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { token?: string; error?: string };
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    if (data.get("newPassword") !== data.get("confirm")) {
      setError(new Error(t("auth.passwordMismatch")));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await authPost("/reset-password", { token: search.token, newPassword: data.get("newPassword") });
      await navigate({ to: "/login", search: { reset: 1 } as never });
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };
  if (!search.token || search.error) {
    return (
      <AuthCard title={t("auth.resetTitle")}>
        <div className="space-y-4">
          <ErrorBox error={new Error(t("auth.linkInvalid"))} />
          <Link to="/forgot-password" className="block text-center text-sm font-medium text-brand-600 hover:underline">
            {t("auth.requestNewLink")}
          </Link>
        </div>
      </AuthCard>
    );
  }
  return (
    <AuthCard title={t("auth.resetTitle")} subtitle={t("auth.resetSubtitle")}>
      <form onSubmit={submit} className="space-y-4">
        <Field label={t("settings.newPassword")} help={t("auth.passwordHint")} htmlFor="newPassword">
          <Input id="newPassword" name="newPassword" type="password" minLength={10} required autoComplete="new-password" autoFocus />
        </Field>
        <Field label={t("auth.confirmPassword")} htmlFor="confirm">
          <Input id="confirm" name="confirm" type="password" minLength={10} required autoComplete="new-password" />
        </Field>
        <ErrorBox error={error} />
        <Button type="submit" className="w-full justify-center" loading={busy}>
          {t("auth.resetSubmit")}
        </Button>
      </form>
    </AuthCard>
  );
}

function BackToLogin() {
  const { t } = useTranslation();
  return (
    <Link to="/login" className="block text-center text-sm text-fg-muted hover:underline">
      {t("auth.backToLogin")}
    </Link>
  );
}
